'use client';

import * as Sentry from '@sentry/nextjs';
import classNames from 'classnames';
import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Country } from '@/app/enums';
import { useAbGroup } from '@/app/providers/ConfigProvider';
import { PropsWithClassname } from '@/app/types';
import { getCountryConfig } from '@/app/utils/countryConfig';

import {
  getTipOptionsForHigherAmountBasedOnGroup,
  getTipOptionsForLowerAmountBasedOnGroup,
} from '@/app/utils/abTesting/checkout-form';

import PercentageBreakpointsInput from '@/app/components/PercentageBreakpointsInput/PercentageBreakpointsInput';

export enum TipForDonioInputLevel {
  LOW_AMOUNT_GIVEN = 'low-amount-given',
  HIGH_AMOUNT_GIVEN = 'higher-amount-given',
}

export interface TipForDonioInputProps extends PropsWithClassname {
  value: number;
  onChange: (nextValue: number) => void;
  label?: string;
  disabled?: boolean;
  level?: TipForDonioInputLevel;
  isErrorVisible?: boolean;
  errorMessage?: ReactNode;
  inputForZeroTipPercentage?: ReactNode;
  displayedValueOverride?: ReactNode;
}

const countryConfig = getCountryConfig();

const TipForDonioInput: FC<TipForDonioInputProps> = ({
  onChange,
  label,
  disabled,
  className,
  value,
  level = TipForDonioInputLevel.LOW_AMOUNT_GIVEN,
  isErrorVisible,
  errorMessage,
  inputForZeroTipPercentage,
  displayedValueOverride,
}) => {
  const { t } = useTranslation();
  const abGroup = useAbGroup();

  const inCountryTranslation = useMemo(() => {
    const country = countryConfig.country;

    switch (country) {
      case Country.CZECH_REPUBLIC:
        return t('components.TipForDonioInput.inCzechia');

      case Country.SLOVAKIA:
        return t('components.TipForDonioInput.inSlovakia');

      case Country.SPAIN:
        return t('components.TipForDonioInput.inSpain');

      default:
        Sentry.captureException(
          `Unsupported country "${country}" defined in environment variables for TipForDonioInput resolver.`,
        );
        return '';
    }
  }, [countryConfig, t]);

  const valueDisplay = useMemo(() => {
    if (displayedValueOverride) {
      return displayedValueOverride;
    }

    if (value === 0) {
      return t('components.TipForDonioInput.nothing');
    }

    return `${value} %`;
  }, [displayedValueOverride, value, t]);

  const options = useMemo(() => {
    switch (level) {
      case TipForDonioInputLevel.HIGH_AMOUNT_GIVEN:
        const optionsForHigherAmountWithTranslationKeys = getTipOptionsForHigherAmountBasedOnGroup(abGroup);

        return optionsForHigherAmountWithTranslationKeys.map((option) => {
          const { noteTranslationKey, ...rest } = option;

          if (option.value === 0) {
            return {
              ...rest,
              note: (
                <>
                  <span className="block mb-[16px]">
                    {t(noteTranslationKey, { inCountry: inCountryTranslation, count: rest.value })}
                  </span>
                  {inputForZeroTipPercentage}
                </>
              ),
            };
          }

          return {
            ...rest,
            note: t(noteTranslationKey, { inCountry: inCountryTranslation, count: rest.value }),
          };
        });

      case TipForDonioInputLevel.LOW_AMOUNT_GIVEN:
      default:
        const optionsForLowerAmountWithTranslationKeys = getTipOptionsForLowerAmountBasedOnGroup(abGroup);

        return optionsForLowerAmountWithTranslationKeys.map((option) => {
          const { noteTranslationKey, ...rest } = option;

          if (option.value === 0) {
            return {
              ...rest,
              note: (
                <>
                  <span className="block mb-[16px]">
                    {t(noteTranslationKey, { inCountry: inCountryTranslation, count: rest.value })}
                  </span>
                  {inputForZeroTipPercentage}
                </>
              ),
            };
          }

          return {
            ...rest,
            note: t(noteTranslationKey, { inCountry: inCountryTranslation, count: rest.value }),
          };
        });
    }
  }, [level, inCountryTranslation, t]);

  return (
    <div className={classNames(className, { 'has-error': isErrorVisible })}>
      {!!label && <label className="block mb-[16px] font-medium text-[16px] leading-[24px]">{label}</label>}
      {!!errorMessage && isErrorVisible && <div className="text-warning font-medium mb-[8px]">{errorMessage}</div>}
      <PercentageBreakpointsInput
        value={value}
        onChange={onChange}
        options={options}
        disabled={disabled}
        valueDisplay={valueDisplay}
      />
    </div>
  );
};

export default TipForDonioInput;
